import { useState, useEffect, useRef } from 'react';
import { collection, doc, getDoc as nativeGetDoc, getDocs as nativeGetDocs, setDoc as nativeSetDoc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import TextInput from '../formComponents/TextInput';
import { getRequiredJoinDocuments } from '../../teams/utils/documentUtils';
const DEFAULT_QUESTION = {
  id: '',
  label: '',
  type: 'text',
  required: false,
  syncToProfile: false,
  options: []
};
const PRICING_QUESTION_IDS = ['pricingTags', 'pricingTag'];
const isPricingQuestion = q => PRICING_QUESTION_IDS.includes(q?.id);
const ALLOWED_QUESTION_TYPES = ['text', 'textarea', 'number', 'yesno', 'date', 'select', 'checkbox'];
const ALLOWED_PROFILE_FIELD_TYPES = ['text', 'number', 'date', 'select'];
const AUTO_COLLECTED_FIELDS = [{
  key: 'firstName',
  label: 'First name',
  patterns: [/\bfirst\s*name\b/, /\bgiven\s*name\b/, /^first$/]
}, {
  key: 'surname',
  label: 'Surname',
  patterns: [/\bsurname\b/, /\blast\s*name\b/, /\bfamily\s*name\b/]
}, {
  key: 'dob',
  label: 'Date of birth',
  patterns: [/\bdob\b/, /\bdate\s*of\s*birth\b/, /\bbirth\s*date\b/]
}, {
  key: 'email',
  label: 'Email',
  patterns: [/\be[\s-]*mail\b/, /\bemail\s*address\b/]
}, {
  key: 'phone',
  label: 'Phone number',
  patterns: [/\bphone\b/, /\bphone\s*number\b/, /\bmobile\b/, /\btelephone\b/, /\btel\b/]
}, {
  key: 'emergencyContacts',
  label: 'Emergency contacts',
  patterns: [/\bemergency\s*contact(s)?\b/, /\bnext\s*of\s*kin\b/]
}];
function normalizeLabel(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}
function normalizeOptions(options) {
  return Array.isArray(options) ? options.map(opt => String(opt || '').trim()).filter(Boolean) : [];
}
function detectAutoCollectedFields(label) {
  const normalized = normalizeLabel(label);
  if (!normalized) return [];
  return AUTO_COLLECTED_FIELDS.filter(field => field.patterns.some(pattern => pattern.test(normalized)));
}
function normalizeQuestionForEditor(question = {}) {
  const type = ALLOWED_QUESTION_TYPES.includes(question?.type) ? question.type : 'text';
  return {
    ...question,
    type,
    required: type === 'checkbox' ? true : question?.required === true,
    syncToProfile: question?.syncToProfile === true,
    options: type === 'select' ? normalizeOptions(question?.options) : []
  };
}
function mapProfileFieldToJoinQuestion(field = {}) {
  const options = normalizeOptions(field?.options);
  const lowered = options.map(opt => opt.toLowerCase());
  const isYesNoSelect = options.length === 2 && lowered.includes('yes') && lowered.includes('no');
  let type = 'text';
  if (field?.type === 'number') type = 'number';else if (field?.type === 'date') type = 'date';else if (field?.type === 'select') type = isYesNoSelect ? 'yesno' : 'select';
  return {
    id: field.id || crypto.randomUUID(),
    label: String(field?.label || field?.id || '').trim(),
    type,
    required: !!field?.required,
    syncToProfile: true,
    options: type === 'select' ? options : []
  };
}
function mapJoinQuestionToProfileField(question = {}) {
  const id = typeof question?.id === 'string' ? question.id.trim() : '';
  const label = typeof question?.label === 'string' ? question.label.trim() : '';
  if (!id || !label) return null;
  if (question?.type === 'number') {
    return {
      id,
      label,
      type: 'number',
      required: !!question?.required,
      options: []
    };
  }
  if (question?.type === 'date') {
    return {
      id,
      label,
      type: 'date',
      required: !!question?.required,
      options: []
    };
  }
  if (question?.type === 'yesno') {
    return {
      id,
      label,
      type: 'select',
      required: !!question?.required,
      options: ['yes', 'no']
    };
  }
  if (question?.type === 'select') {
    const options = normalizeOptions(question?.options);
    if (options.length === 0) return null;
    return {
      id,
      label,
      type: 'select',
      required: !!question?.required,
      options
    };
  }
  if (question?.type === 'checkbox') {
    return {
      id,
      label,
      type: 'select',
      required: !!question?.required,
      options: ['yes', 'no']
    };
  }
  return {
    id,
    label,
    type: 'text',
    required: !!question?.required,
    options: []
  };
}
export default function EditJoinFormConfig({
  teamId,
  isOpen,
  onClose,
  onSave,
  dataAccess = null,
}) {
  const { getDoc, getDocs, setDoc } = dataAccess || { getDoc: nativeGetDoc, getDocs: nativeGetDocs, setDoc: nativeSetDoc };
  const [enabled, setEnabled] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [profileFields, setProfileFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [warningPopup, setWarningPopup] = useState(null);
  const [hasRequiredJoinDocuments, setHasRequiredJoinDocuments] = useState(false);
  const dismissedWarningKeysRef = useRef(new Set());
  useEffect(() => {
    if (!isOpen) return;
    async function fetchConfig() {
      setLoading(true);
      try {
        const teamRef = doc(db, 'teams', teamId);
        const joinRef = doc(db, 'teams', teamId, 'joinFormConfig', 'config');
        const profileRef = doc(db, 'teams', teamId, 'profileFields', 'config');
        const documentsRef = collection(db, 'teams', teamId, 'documents');
        const [teamSnap, joinSnap, profileSnap, documentsSnap] = await Promise.all([getDoc(teamRef), getDoc(joinRef), getDoc(profileRef), getDocs(documentsRef)]);
        const documentsEnabled = Boolean(teamSnap.exists() && teamSnap.data()?.modules?.documents?.enabled);
        const requiredJoinDocuments = getRequiredJoinDocuments(documentsSnap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        })));
        const hasRequiredDocuments = documentsEnabled && requiredJoinDocuments.length > 0;
        setHasRequiredJoinDocuments(hasRequiredDocuments);
        if (joinSnap.exists()) {
          const data = joinSnap.data();
          const qs = (data.questions ?? []).map(normalizeQuestionForEditor);
          const hasPricingTag = qs.some(isPricingQuestion);
          setEnabled(hasPricingTag || hasRequiredDocuments ? true : data.enabled ?? true);
          setQuestions(qs);
        } else {
          setEnabled(hasRequiredDocuments);
          setQuestions([]);
        }
        const profileData = profileSnap.exists() ? profileSnap.data() || {} : {};
        const loadedProfileFields = Array.isArray(profileData.fields) ? profileData.fields.map(field => ({
          ...field,
          id: typeof field?.id === 'string' ? field.id.trim() : '',
          label: String(field?.label || '').trim(),
          type: field?.type === 'qualification' ? 'qualification' : ALLOWED_PROFILE_FIELD_TYPES.includes(field?.type) ? field.type : 'text',
          required: !!field?.required,
          options: normalizeOptions(field?.options)
        })).filter(field => field.id && field.label) : [];
        setProfileFields(loadedProfileFields);
      } catch (err) {
        setError('Failed to load form config.');
      } finally {
        setLoading(false);
      }
    }
    dismissedWarningKeysRef.current.clear();
    setWarningPopup(null);
    fetchConfig();
  }, [teamId, isOpen, getDoc, getDocs]);
  const handleAddQuestion = () => {
    setQuestions(prev => [...prev, {
      ...DEFAULT_QUESTION,
      id: crypto.randomUUID()
    }]);
  };
  const handleUpdateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    if (field === 'type' && value !== 'select') {
      updated[index].options = [];
    }
    if (field === 'type' && value === 'checkbox') {
      updated[index].required = true;
    }
    setQuestions(updated);
    if (field !== 'label') return;
    const questionId = updated[index]?.id || `question-${index}`;
    const matches = detectAutoCollectedFields(value);
    if (matches.length === 0) {
      setWarningPopup(prev => prev?.questionId === questionId ? null : prev);
      return;
    }
    const warningKey = `${questionId}:${matches.map(m => m.key).sort().join('|')}`;
    if (dismissedWarningKeysRef.current.has(warningKey)) return;
    setWarningPopup({
      questionId,
      label: value,
      matches,
      warningKey
    });
  };
  const handleRemoveQuestion = index => {
    const question = questions[index];
    if (!window.confirm(`Remove "${question?.label || 'this join-form question'}" when you save? Existing submitted answers will no longer appear through this question.`)) return;
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
  };
  const toggleProfileFieldQuestion = (field, shouldInclude) => {
    const fieldId = typeof field?.id === 'string' ? field.id.trim() : '';
    if (!fieldId) return;
    if (shouldInclude) {
      if (questions.some(question => question.id === fieldId)) return;
      setQuestions(prev => [...prev, mapProfileFieldToJoinQuestion(field)]);
      return;
    }
    setQuestions(prev => prev.filter(question => question.id !== fieldId));
  };
  const dismissWarningPopup = () => {
    if (warningPopup?.warningKey) {
      dismissedWarningKeysRef.current.add(warningPopup.warningKey);
    }
    setWarningPopup(null);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const cleanedQuestions = questions.filter(q => isPricingQuestion(q) || (typeof q.label === 'string' && q.label.trim() !== '' && typeof q.id === 'string' && ALLOWED_QUESTION_TYPES.includes(q.type))).map(q => {
        if (isPricingQuestion(q)) return q;
        const type = ALLOWED_QUESTION_TYPES.includes(q.type) ? q.type : 'text';
        const options = type === 'select' ? normalizeOptions(q.options) : [];
        return {
          id: q.id,
          label: q.label.trim(),
          type,
          required: type === 'checkbox' ? true : !!q.required,
          syncToProfile: q?.syncToProfile === true,
          options
        };
      }).filter(q => isPricingQuestion(q) || q.type !== 'select' || (q.options || []).length > 0);
      const hasPricingTag = cleanedQuestions.some(isPricingQuestion);
      const enforcedEnabled = hasPricingTag || hasRequiredJoinDocuments ? true : enabled;
      await setDoc(doc(db, 'teams', teamId, 'joinFormConfig', 'config'), {
        enabled: enforcedEnabled,
        questions: cleanedQuestions
      });
      const profileMap = new Map(profileFields.map(field => [field.id, field]));
      cleanedQuestions.filter(question => !isPricingQuestion(question) && question.syncToProfile === true).forEach(question => {
        const mapped = mapJoinQuestionToProfileField(question);
        if (!mapped) return;
        const existing = profileMap.get(mapped.id);
        if (existing?.type === 'qualification') return;
        profileMap.set(mapped.id, {
          ...(existing || {}),
          ...mapped
        });
      });
      const mergedProfileFields = Array.from(profileMap.values()).filter(field => {
        const id = typeof field?.id === 'string' ? field.id.trim() : '';
        const label = typeof field?.label === 'string' ? field.label.trim() : '';
        return id !== '' && label !== '';
      });
      if (mergedProfileFields.length > 0) {
        await setDoc(doc(db, 'teams', teamId, 'profileFields', 'config'), {
          enabled: true,
          fields: mergedProfileFields
        }, {
          merge: true
        });
      }
      if (onSave) await onSave();
      onClose();
    } catch (err) {
      setError('Failed to save form config.');
    } finally {
      setSaving(false);
    }
  };
  if (!isOpen) return null;
  return <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Edit Join Form</h2>
        {error && <ErrorMessage message={error} />}
        {loading ? <p>Loading...</p> : <form onSubmit={handleSubmit}>
            <label>
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={questions.some(isPricingQuestion) || hasRequiredJoinDocuments} />{' '}
              Enable Join Form
            </label>

            {(questions.some(isPricingQuestion) || hasRequiredJoinDocuments) && <div className="form-help">
                <em>Join form must remain enabled while pricing options or required join documents are active.</em>
              </div>}

            <div className="form-help auto-collected-note">
              <strong>Collected automatically:</strong> first name, surname, date of birth, email, phone number, and emergency contacts.
              Please avoid adding these as custom join questions.
            </div>

            {profileFields.some(field => field.type !== 'qualification') && <div className="form-help auto-collected-note" style={{
              marginTop: '0.75rem'
        }}>
                <strong>Include existing profile fields in this join form:</strong>
                <div style={{
            display: 'grid',
            gap: '0.35rem',
            marginTop: '0.5rem'
          }}>
                  {profileFields.filter(field => field.type !== 'qualification').map(field => {
              const included = questions.some(question => question.id === field.id);
              return <label key={field.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                        <input type="checkbox" checked={included} onChange={e => toggleProfileFieldQuestion(field, e.target.checked)} />
                        <span>
                          {field.label}
                          {field.required ? ' *' : ''}
                        </span>
                      </label>;
            })}
                </div>
              </div>}

            {warningPopup && <div className="join-form-warning-popup" role="alert" aria-live="polite">
                <p>
                  <strong>Potential duplicate field:</strong> "{warningPopup.label?.trim() || 'This label'}" looks like{' '}
                  {warningPopup.matches.map(item => item.label).join(', ')}, which is already collected automatically.
                </p>
                <button type="button" className="join-form-warning-dismiss" onClick={dismissWarningPopup}>
                  Dismiss this warning
                </button>
              </div>}

            {questions.map((q, index) => {
          const isPricingTag = isPricingQuestion(q);
          const autoCollectedMatches = isPricingTag ? [] : detectAutoCollectedFields(q.label);
          return <div key={q.id} className={`form-question-editor${autoCollectedMatches.length > 0 ? ' auto-collected-match' : ''}`} style={{
            opacity: isPricingTag ? 0.8 : 1
          }}>
                  <TextInput label="Question Label" value={q.label} onChange={e => !isPricingTag && handleUpdateQuestion(index, 'label', e.target.value)} disabled={isPricingTag} />

                  {autoCollectedMatches.length > 0 && <div className="form-help form-help-warning">
                      This looks like {autoCollectedMatches.map(item => item.label).join(', ')}, which is already collected automatically.
                    </div>}

                  <div className="field-row">
                    <div>
                      <label>Type</label>
                      <select value={q.type} onChange={e => !isPricingTag && handleUpdateQuestion(index, 'type', e.target.value)} disabled={isPricingTag}>
                        <option value="text">Short Text</option>
                        <option value="textarea">Paragraph</option>
                        <option value="number">Number</option>
                        <option value="yesno">Yes / No</option>
                        <option value="date">Date</option>
                        <option value="select">Select</option>
                        <option value="checkbox">Checkbox / Acknowledgement</option>
                      </select>
                    </div>

                    <div>
                      <label>
                        <input type="checkbox" checked={q.required} onChange={e => !isPricingTag && handleUpdateQuestion(index, 'required', e.target.checked)} disabled={isPricingTag || q.type === 'checkbox'} />{' '}
                        Required
                      </label>
                    </div>

                    <div>
                      <label>
                        <input type="checkbox" checked={q.syncToProfile === true} onChange={e => !isPricingTag && handleUpdateQuestion(index, 'syncToProfile', e.target.checked)} disabled={isPricingTag} />{' '}
                        Sync to member profile
                      </label>
                    </div>
                  </div>

                  {q.type === 'select' && <TextInput label="Options (comma separated)" value={(q.options || []).join(', ')} onChange={e => !isPricingTag && handleUpdateQuestion(index, 'options', e.target.value.split(',').map(opt => opt.trim()).filter(Boolean))} disabled={isPricingTag} />}
                  {q.type === 'checkbox' && <div className="form-help">
                      <em>This renders as a required single checkbox using the question label as the statement. Use this to ensure members agree to something. If you sync it to the profile, it will be stored there as a yes/no field. If you want them to adhere to a document, add that from the Documents section.</em>
                    </div>}

                  {isPricingTag ? <div className="form-help">
                      <em>This question is auto-managed based on your pricing settings and cannot be edited manually.</em>
                    </div> : <button type="button" className="remove-btn" onClick={() => handleRemoveQuestion(index)}>
                      Remove This Question
                    </button>}
                </div>;
        })}

            <button type="button" className="add-question-btn" onClick={handleAddQuestion}>
              Add a New Question
            </button>

            <div className="form-actions" style={{
          marginTop: '1.5rem',
          display: 'flex',
          gap: '1rem'
        }}>
              <SubmitButton type="button" onClick={onClose} label="Cancel" variant="ghost" size="sm" />
              <SubmitButton type="submit" label={saving ? 'Saving…' : 'Save Join Form'} disabled={saving} variant="primary" />
            </div>
          </form>}
      </div>
    </div>;
}
